import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserMultiFormatReader } from '@zxing/library';
import { FiX, FiCamera } from 'react-icons/fi';
import FullScreenOverlay from './ui/FullScreenOverlay';

const QRScanner = ({ onScan, onClose }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const codeReaderRef = useRef(null);

  useEffect(() => {
    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;

    const startScanning = async () => {
      try {
        setScanning(true);
        
        // Get available video devices
        const videoInputDevices = await codeReader.listVideoInputDevices();
        
        // Prefer back camera if available
        const selectedDeviceId = videoInputDevices.length > 0 
          ? videoInputDevices[videoInputDevices.length - 1].deviceId 
          : undefined;

        // Start continuous decode from video device
        await codeReader.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current,
          (result, err) => {
            if (result) {
              setScanning(false);
              onScan(result.getText());
              codeReader.reset();
            }
            if (err && !(err.name === 'NotFoundException')) {
              console.error('Scan error:', err);
            }
          }
        );
      } catch (err) {
        console.error('Error starting scanner:', err);
        setError(t('cm.camera.access_failed'));
        setScanning(false);
      }
    };

    startScanning();

    return () => {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
    };
  }, [onScan, t]);

  // FullScreenOverlay portals to <body>, matching the sibling scanners: this
  // overlay is opened from inside modal panels whose transform would otherwise
  // trap a plain `fixed` root. Safe-area padding keeps the header clear of the
  // notch and the tip card clear of the home indicator on a phone.
  return (
    <FullScreenOverlay zClassName="z-[60]" bgClassName="bg-black/75" className="overflow-y-auto backdrop-blur-sm">
      <div className="flex min-h-full items-stretch sm:items-center justify-center p-0 sm:p-4">
        <div className="relative bg-surface text-content shadow-2xl max-w-2xl w-full sm:rounded-2xl">
        {/* Close Button */}
        <button
          onClick={() => {
            if (codeReaderRef.current) {
              codeReaderRef.current.reset();
            }
            onClose();
          }}
          className="absolute top-[max(1rem,env(safe-area-inset-top))] sm:top-4 end-4 z-10 flex items-center justify-center touch-target shrink-0 rounded-full bg-surface-2 border border-edge text-content-secondary hover:text-content hover:bg-surface-3 transition-colors shadow-lg"
        >
          <FiX size={24} />
        </button>

        {/* Header */}
        <div className="p-6 pt-[max(1.5rem,env(safe-area-inset-top))] sm:pt-6 border-b border-edge bg-surface-2 sm:rounded-t-2xl">
          <div className="flex items-center justify-center gap-3 text-content">
            <FiCamera size={28} className="shrink-0" />
            <h3 className="text-xl font-bold truncate">{t('cm.qr.scan_title')}</h3>
          </div>
          <p className="text-center text-content-secondary mt-2">
            {t('cm.qr.position_hint')}
          </p>
        </div>

        {/* Scanner Area */}
        <div className="p-6">
          {error ? (
            <div className="text-center py-12">
              <div className="bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-lg p-4 mb-4">
                <FiCamera size={48} className="mx-auto mb-3 opacity-50 text-rose-600 dark:text-rose-400" />
                <p className="font-semibold text-rose-600 dark:text-rose-400">{error}</p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="min-h-[44px] px-6 py-3 bg-accent text-white dark:text-slate-900 rounded-lg font-semibold hover:bg-accent-hover transition-colors"
              >
                {t('cm.retry')}
              </button>
            </div>
          ) : (
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ maxHeight: '50vh' }}>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
                autoPlay
              />
              
              {/* Scanning overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 border-4 border-accent opacity-50 animate-pulse"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-64 h-64 border-4 border-white opacity-75 rounded-lg"></div>
                </div>
                {scanning && (
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <div className="inline-block bg-black/75 text-white px-4 py-2 rounded-full">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span className="text-sm">{t('cm.scanning')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <div className="bg-accent-soft border border-accent/30 rounded-lg p-4">
            <p className="text-sm text-accent-hover dark:text-accent">
              <strong>{t('cm.tip_label')}</strong> {t('cm.qr.tip_body')}
            </p>
          </div>
        </div>
      </div>
      </div>
    </FullScreenOverlay>
  );
};

export default QRScanner;
